/**
 * <planet-table> — grahas with sidereal longitude in rāśi-D-M-S, nakṣatra, pada,
 * navāṁśa, dignity, retrogression and combustion. Sortable, and the printable
 * core of any reading.
 *
 * ◆ This table, not the chart, is what another practitioner checks. A square
 * chart shows rāśis; this shows the degrees a disagreement would be about. It is
 * therefore the thing that has to survive printing, copy-paste and being read
 * without the site's CSS.
 *
 * ⚠ It shows the **ayanāṁśa** in its caption. A planet table without one is not
 * checkable against anybody else's — the same birth under Lahiri and KP differs
 * by about 6′, which is enough to move a pada.
 *
 * © Manish Jagdish Thatte
 */

import { GRAHA_IDS, graha } from '../jyotish/constants.js';
import { formatDMS, formatRashiDMS } from '../utils/format.js';
import { localName } from '../utils/i18n.js';

const COLUMNS = [
  { key: 'graha', label: 'Graha', sort: (g) => GRAHA_IDS.indexOf(g.graha) },
  { key: 'position', label: 'Position', sort: (g) => g.lon },
  { key: 'nakshatra', label: 'Nakṣatra', sort: (g) => g.nakshatra.index },
  { key: 'pada', label: 'Pada', sort: (g) => g.padaNumber },
  { key: 'navamsha', label: 'Navāṁśa', sort: (g) => g.navamshaRashiIndex },
  { key: 'dignity', label: 'Dignity', sort: (g) => g.dignity.dignity },
  { key: 'bhava', label: 'Bhāva', sort: (g) => g.bhava ?? 99 },
  { key: 'speed', label: 'Speed', sort: (g) => g.speed },
  { key: 'state', label: 'State', sort: (g) => (g.retrograde ? 1 : 0) + (g.combust ? 2 : 0) }
];

export class PlanetTable extends HTMLElement {
  static observedAttributes = ['locale'];

  connectedCallback() { this.render(); }
  attributeChangedCallback() { if (this.isConnected) this.render(); }

  set kundali(value) { this._kundali = value; this._sort ??= 'graha'; this.render(); }
  get kundali() { return this._kundali ?? null; }

  render() {
    const k = this._kundali;
    this.textContent = '';
    if (!k) return;
    const locale = this.getAttribute('locale') || 'en';
    const sortKey = this._sort ?? 'graha';
    const desc = this._desc ?? false;

    const table = document.createElement('table');
    table.className = 'planet-table';

    const caption = document.createElement('caption');
    caption.className = 'planet-table__caption';
    caption.textContent =
      `Sidereal positions · ${k.settings.ayanamsa} ayanāṁśa ` +
      `${formatDMS(k.ayanamsaDeg)} · ${k.settings.houseSystem} bhāvas · ` +
      `${k.settings.nodeMode} node`;
    table.append(caption);

    const thead = document.createElement('thead');
    const hrow = document.createElement('tr');
    for (const col of COLUMNS) {
      // The bhāva column is meaningless without houses; omit rather than show
      // a column of dashes.
      if (col.key === 'bhava' && !k.bhavas) continue;
      const th = document.createElement('th');
      th.scope = 'col';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'planet-table__sort';
      button.textContent = col.label;
      if (sortKey === col.key) {
        th.setAttribute('aria-sort', desc ? 'descending' : 'ascending');
        button.dataset.active = 'true';
      }
      button.addEventListener('click', () => {
        this._desc = this._sort === col.key ? !this._desc : false;
        this._sort = col.key;
        this.render();
      });
      th.append(button);
      hrow.append(th);
    }
    thead.append(hrow);
    table.append(thead);

    const column = COLUMNS.find((c) => c.key === sortKey) ?? COLUMNS[0];
    const rows = GRAHA_IDS.map((id) => k.grahas[id])
      .sort((a, b) => {
        const va = column.sort(a), vb = column.sort(b);
        const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
        return desc ? -cmp : cmp;
      });

    const tbody = document.createElement('tbody');
    for (const g of rows) {
      const tr = document.createElement('tr');
      tr.dataset.graha = g.graha;
      if (g.retrograde) tr.classList.add('is-retrograde');
      if (g.combust) tr.classList.add('is-combust');

      cell(tr, 'th', localName(graha(g.graha).name, locale), { scope: 'row' });
      cell(tr, 'td', formatRashiDMS(g.lon, { locale }), { class: 'is-numeric' });
      cell(tr, 'td', localName(g.nakshatra.name, locale));
      cell(tr, 'td', `${g.nakshatra.pada}`, { class: 'is-numeric' });
      cell(tr, 'td', g.navamshaRashi.abbr);
      cell(tr, 'td', dignityLabel(g.dignity));
      if (k.bhavas) cell(tr, 'td', String(g.bhava), { class: 'is-numeric' });
      // ⚠ Speed carries its sign. A negative value IS the retrogression, and
      // showing |speed| beside an "R" flag would hide the measurement behind a
      // label — which is the opposite of what this table is for.
      cell(tr, 'td', `${g.speed >= 0 ? '+' : '−'}${Math.abs(g.speed).toFixed(4)}°/d`,
        { class: 'is-numeric' });
      cell(tr, 'td', stateLabel(g));
      tbody.append(tr);
    }
    table.append(tbody);
    this.append(table);
  }
}

function cell(row, tag, text, attrs = {}) {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  node.textContent = text;
  row.append(node);
  return node;
}

const DIGNITY_LABELS = {
  exalted: 'exalted', debilitated: 'debilitated', moolatrikona: 'mūlatrikoṇa',
  ownSign: 'own sign', friend: 'friend’s', neutral: 'neutral', enemy: 'enemy’s',
  unrated: '—'
};

function dignityLabel(d) {
  return DIGNITY_LABELS[d.dignity] ?? d.dignity;
}

function stateLabel(g) {
  const parts = [];
  // ◆ Rāhu and Ketu are always retrograde, and saying so plainly is better than
  // an R the reader has to know to discount.
  if (g.retrograde) parts.push(g.retrogradeByMeasurement ? 'retrograde' : 'always retrograde');
  if (g.combust) parts.push('combust');
  return parts.join(', ') || '—';
}

customElements.define('planet-table', PlanetTable);
